import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { useSettingsContext } from "../SettingsProvider";
import { SettingsPageHeader, FloatingSaveButton } from "../components";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

type SchedulerSettings = {
  promptVisit: {
    when?: number;
    message?: string;
  };
};

export const SchedulerSettings = () => {
  const { settings, setSettings } = useSettingsContext();

  // Memoize original values to prevent infinite loops
  const originalSchedulerSettings = useMemo(() => {
    const schedulerSettings = (settings as any)?.schedule;
    return {
      promptVisit: {
        when: schedulerSettings?.promptVisit?.when,
        message: schedulerSettings?.promptVisit?.message || "",
      },
    } as SchedulerSettings;
  }, [settings]);

  // Local state for form data
  const [when, setWhen] = useState<number | undefined>(undefined);
  const [whenInput, setWhenInput] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Generic helper to set/clear validation errors for any field
  const setFieldError = (field: string, message?: string) => {
    setValidationErrors((prev) => {
      const next = { ...prev } as Record<string, string>;
      if (!message) delete next[field];
      else next[field] = message;
      return next;
    });
  };

  // Initialize local state when settings change
  useEffect(() => {
    const initialWhen = originalSchedulerSettings.promptVisit.when;
    // Convert milliseconds to minutes for display
    const minutes = typeof initialWhen === "number" && !isNaN(initialWhen) 
      ? initialWhen / (60 * 1000) 
      : undefined;
    setWhen(initialWhen);
    setWhenInput(
      minutes !== undefined ? String(minutes) : ""
    );
    setMessage(originalSchedulerSettings.promptVisit.message || "");
    setIsInitialized(true);
  }, [originalSchedulerSettings]);

  // Calculate if data is dirty using useMemo to prevent infinite loops
  const isDirty = useMemo(() => {
    // Only check for changes if we have initialized the local state
    if (!isInitialized) {
      return false;
    }

    // Convert minutes input to milliseconds for comparison
    const parsedMinutes = whenInput.trim() === "" ? undefined : Number(whenInput);
    const currentWhenMs = parsedMinutes !== undefined ? parsedMinutes * 60 * 1000 : undefined;

    const currentSettings = {
      promptVisit: {
        when: currentWhenMs,
        message: message || undefined,
      },
    };

    const originalSettings = {
      promptVisit: {
        when: originalSchedulerSettings.promptVisit.when || undefined,
        message: originalSchedulerSettings.promptVisit.message || undefined,
      },
    };

    return JSON.stringify(currentSettings) !== JSON.stringify(originalSettings);
  }, [whenInput, message, originalSchedulerSettings, isInitialized]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Validate before save - parse minutes and convert to milliseconds
      const parsedMinutes = whenInput.trim() === "" ? undefined : Number(whenInput);
      if (whenInput.trim() !== "" && (isNaN(parsedMinutes as number) || (parsedMinutes as number) < 0)) {
        setFieldError("when", "Invalid value");
        return;
      }
      if (message.length > 144) {
        setFieldError("message", "Message must be 144 characters or less");
        return;
      }
      // Clear any prior errors on successful validation
      setFieldError("when");
      setFieldError("message");

      // Convert minutes to milliseconds for storage
      const whenMs = parsedMinutes !== undefined ? parsedMinutes * 60 * 1000 : undefined;

      await setSettings({
        schedule: {
          promptVisit: {
            when: whenMs,
            message: message || undefined,
          },
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3, pb: 10 }}>
        <Stack spacing={4} sx={{ width: "100%" }}>
          {/* Header with dirty indicator */}
          <SettingsPageHeader title="Scheduler Settings" isDirty={isDirty} />

          {/* Prompt Visit Section */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h5">Prompt Visit</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      This section handles messages to patients if they haven't been triaged by some time.
                    </Typography>
                  </CardContent>
                </Card>

                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    When (minutes)
                  </Typography>
                  <TextField
                    fullWidth
                    label="When"
                    type="text"
                    value={whenInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Allow empty; otherwise allow digits and decimal point
                      if (raw === "") {
                        setWhenInput("");
                        setWhen(undefined);
                        setFieldError("when");
                        return;
                      }
                      // Allow digits and one decimal point
                      const cleaned = raw.replace(/[^\d.]/g, "");
                      // Only allow one decimal point
                      const parts = cleaned.split(".");
                      const sanitized = parts.length > 2 
                        ? parts[0] + "." + parts.slice(1).join("")
                        : cleaned;
                      setWhenInput(sanitized);
                      if (sanitized === "" || sanitized === ".") {
                        setWhen(undefined);
                        setFieldError("when");
                      } else {
                        const num = Number(sanitized);
                        setWhen(num * 60 * 1000); // Store as milliseconds internally
                        setFieldError("when", isNaN(num) || num < 0 ? "Invalid value" : undefined);
                      }
                    }}
                    onBlur={() => {
                      const trimmed = whenInput.trim();
                      if (trimmed === "" || trimmed === ".") {
                        setFieldError("when");
                        return;
                      }
                      // Normalize the value
                      const num = Number(trimmed);
                      if (isNaN(num) || num < 0) {
                        setFieldError("when", "Invalid value");
                        return;
                      }
                      const normalized = String(num);
                      setWhenInput(normalized);
                      setWhen(num * 60 * 1000); // Store as milliseconds internally
                      setFieldError("when");
                    }}
                    variant="outlined"
                    error={Boolean(validationErrors.when)}
                    helperText={
                      validationErrors.when || "Time in minutes when patients should be prompted"
                    }
                    sx={{ maxWidth: 400 }}
                    inputMode="decimal"
                    inputProps={{ inputMode: "decimal", pattern: "[0-9.]*", min: 0, step: 0.1 }}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Message
                  </Typography>
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={4}
                    value={message}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue.length <= 144) {
                        setMessage(newValue);
                        setFieldError("message");
                      } else {
                        setFieldError("message", "Message must be 144 characters or less");
                      }
                    }}
                    variant="outlined"
                    error={Boolean(validationErrors.message)}
                    helperText={
                      validationErrors.message ||
                      `${message.length}/144 characters (SMS limit).`
                    }
                    inputProps={{ maxLength: 144 }}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>

        {/* Floating Save Button */}
        <FloatingSaveButton show={isDirty} onSave={handleSave} isSaving={isSaving} />
      </Box>
    </Box>
  );
};

